import React, { useState, useEffect, useRef } from 'react';
import ProgressBar from './ProgressBar';
import { api } from '../api';

export default function JobQueue() {
  const [activeJobs, setActiveJobs] = useState([]);
  const [recentJobs, setRecentJobs] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loadError, setLoadError] = useState('');
  const wsState = useRef({});

  useEffect(() => {
    loadJobs();
    const interval = setInterval(loadJobs, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const unsubscribe = api.onJobUpdate('queue', (data) => {
      if (data.type === 'job_progress') {
        wsState.current[data.job_id] = { progress: data.progress, processed: data.processed, total: data.total, message: data.message };
        setActiveJobs(prev => prev.map(job =>
          job.id === data.job_id ? { ...job, ...wsState.current[data.job_id], status: 'running' } : job
        ));
      } else if (data.type === 'job_started') {
        setActiveJobs(prev => {
          const exists = prev.find(j => j.id === data.job_id);
          if (!exists) return [...prev, { id: data.job_id, type: data.job_type, status: 'running', progress: 0, message: 'Starting...' }];
          return prev.map(j => j.id === data.job_id ? { ...j, status: 'running' } : j);
        });
      } else if (data.type === 'job_completed' || data.type === 'job_failed') {
        setActiveJobs(prev => prev.filter(j => j.id !== data.job_id));
        setRecentJobs(prev => [{
          id: data.job_id, type: data.job_type,
          status: data.type === 'job_completed' ? 'completed' : 'failed',
          error: data.error || null, completed_at: new Date().toISOString(),
        }, ...prev].slice(0, 5));
        delete wsState.current[data.job_id];
      }
    });
    return unsubscribe;
  }, []);

  const loadJobs = async () => {
    try {
      const data = await api.getJobs();
      if (data.active_jobs) {
        setActiveJobs(() => data.active_jobs.map(job => {
          const ws = wsState.current[job.id];
          return ws ? { ...job, ...ws, status: 'running' } : job;
        }));
      }
      if (data.recent_jobs) setRecentJobs(data.recent_jobs.slice(0, 5));
      setLoadError('');
    } catch (err) {
      setLoadError(err.message || 'Failed to load jobs');
    }
  };

  // Optimistic cancel — drop from activeJobs + add a "Cancelled" recent row
  // immediately so the UI feels responsive even if the server takes a beat
  // to fire the job_failed websocket event.
  const cancel = async (jobId, jobType) => {
    setActiveJobs(prev => prev.filter(j => j.id !== jobId));
    setRecentJobs(prev => [{
      id: jobId,
      type: jobType || 'job',
      status: 'failed',
      error: 'Cancelled by user',
      completed_at: new Date().toISOString(),
    }, ...prev].slice(0, 5));
    delete wsState.current[jobId];
    try {
      await api.cancelJob(jobId);
    } catch (err) {
      console.error('Cancel API call failed:', err);
    }
  };

  const running = activeJobs.find(j => j.status === 'running');
  const queued = activeJobs.filter(j => j.status === 'queued');

  if (activeJobs.length === 0 && recentJobs.length === 0) {
    return (
      <div className={`border-t border-slate-800 px-6 py-2.5 text-[11px] ${loadError ? 'text-red-400' : 'text-slate-600'}`}>
        {loadError ? `Job queue unavailable: ${loadError}` : 'Queue empty'}
      </div>
    );
  }

  return (
    <div className="border-t border-slate-800 bg-slate-900">
      {/* Running */}
      {running && (
        <div className="px-6 py-3 border-b border-slate-800/50">
          <div className="flex items-center justify-between mb-2">
            <div className="flex-1">
              <span className="text-sm text-slate-200 capitalize">{running.type}</span>
              {running.message && <span className="text-xs text-slate-500 ml-2">{running.message}</span>}
            </div>
            <button onClick={() => cancel(running.id, running.type)}
              className="ml-4 px-2.5 py-1 text-xs text-slate-500 hover:text-red-400 transition-colors">
              Cancel
            </button>
          </div>
          <ProgressBar progress={running.progress || 0} message={running.message || 'Processing...'} status="running" />
        </div>
      )}

      {/* Status bar */}
      <div className="px-6 py-2.5 flex items-center justify-between">
        <span className="text-[11px] text-slate-600">
          {queued.length > 0 ? `${queued.length} queued` : ''}
          {queued.length > 0 && recentJobs.length > 0 ? ' / ' : ''}
          {recentJobs.length > 0 ? `${recentJobs.length} recent` : ''}
          {queued.length === 0 && recentJobs.length === 0 && !running ? 'Queue empty' : ''}
        </span>
        {(queued.length > 0 || recentJobs.length > 0) && (
          <button onClick={() => setExpanded(!expanded)} className="text-[11px] text-slate-500 hover:text-slate-300 transition-colors">
            {expanded ? 'Hide' : 'Details'}
          </button>
        )}
      </div>

      {/* Expanded */}
      {expanded && (
        <div className="px-6 py-3 border-t border-slate-800/50 space-y-3 max-h-48 overflow-y-auto">
          {queued.length > 0 && (
            <div>
              <h5 className="text-[10px] text-slate-600 uppercase tracking-wider mb-1.5">Queued</h5>
              <div className="space-y-1">
                {queued.map((job) => (
                  <div key={job.id} className="flex items-center justify-between text-xs py-1.5 px-2 rounded hover:bg-slate-800/30">
                    <span className="text-slate-400 capitalize">{job.type}</span>
                    <button onClick={() => cancel(job.id, job.type)} className="text-slate-600 hover:text-red-400 text-[11px] transition-colors">Cancel</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {recentJobs.length > 0 && (
            <div>
              <h5 className="text-[10px] text-slate-600 uppercase tracking-wider mb-1.5">Recent</h5>
              <div className="space-y-1">
                {recentJobs.map((job) => (
                  <div key={job.id} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded">
                    <span className={job.status === 'completed' ? 'text-emerald-500' : 'text-red-400'}>
                      {job.status === 'completed' ? 'Done' : 'Failed'}
                    </span>
                    <span className="flex-1 text-slate-400 capitalize">{job.type}</span>
                    {job.error && <span className="text-[11px] text-red-400/60 truncate max-w-xs">{job.error}</span>}
                    {(job.completed_at || job.ended_at) && <span className="text-[11px] text-slate-600">{new Date(job.completed_at || job.ended_at).toLocaleTimeString()}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
